import { IconHeadphones } from "@tabler/icons-react"
import { type FC } from "react"
import { useDropzone } from "react-dropzone"

import { getExtensionByFileName, supportedFileExtensions } from "@forento/core/utilities/file"
import { useFileUrl } from "@forento/ui/hooks/useFileUrl"

import { CheckmarkIcon, DeleteIcon, SubtitlesIcon, UploadFileIcon } from "~/components/Icon"
import InputLabel from "~/components/InputLabel"
import { useAlert } from "~/contexts/AlertContext"

const acceptByType = {
	audio: { "audio/*": supportedFileExtensions.audio.map(x => `.${x.toLowerCase()}`) },
	"video-captions": { ".vtt": [], ".srt": [] },
	file: undefined,
} as const

const iconByType = {
	audio: IconHeadphones,
	"video-captions": SubtitlesIcon,
	file: UploadFileIcon,
} as const

type Type = "audio" | "video-captions" | "file"

type Props = { label?: string; type: Type; value: File | null; onChange(value: File | null): void }
const FileChooser: FC<Props> = ({ label, type, value, onChange }) => {
	const content = value ? (
		<Preview type={type} file={value} onChange={onChange} />
	) : (
		<Dropzone type={type} onChange={onChange} />
	)

	if (!label) return content

	return (
		<div>
			<InputLabel>{label}</InputLabel>
			{content}
		</div>
	)
}

type DropzoneProps = { type: Type; onChange(file: File): void }
const Dropzone: FC<DropzoneProps> = ({ type, onChange }) => {
	const alert = useAlert()

	const dropzone = useDropzone({
		accept: acceptByType[type],
		async onDrop(acceptedFiles: File[]) {
			const file = acceptedFiles[0]
			if (file === undefined) return

			if (file.size > 400 * 1024 * 1024) {
				await alert.show(
					"File too large",
					"The file is too large. It can be a maximum of 400 MB. Please upload a smaller file.",
				)
				return
			}

			onChange(file)
		},
	})

	const Icon = iconByType[type]

	return (
		<div
			className="relative flex h-32 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border border-[#efefef] bg-[#f9f8ff] p-8"
			{...dropzone.getRootProps()}
		>
			<input {...dropzone.getInputProps()} />
			{dropzone.isDragActive ? (
				dropzone.isDragReject ? (
					<p className="text-light text-sm">
						<span className="text-danger font-semibold">That file type</span> is not supported
					</p>
				) : (
					<p className="text-light text-sm">
						<span className="text-primary font-semibold">Drop file</span> to upload
					</p>
				)
			) : (
				<>
					<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-6 border-[#f6f3ff] bg-[#edebf3]">
						<Icon className="text-primary size-6" />
					</div>
					<p className="text-light text-sm">
						<span className="text-primary font-semibold">Click to upload</span> or drag and drop
					</p>
				</>
			)}
		</div>
	)
}

const Preview: FC<{ type: Type; file: File; onChange(file: File | null): void }> = ({ type, file, onChange }) => {
	const Icon = iconByType[type]

	return (
		<div className="border-primary flex items-start gap-4 rounded-lg border bg-white p-4">
			<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-4 border-[#f6f3ff] bg-[#edebf3]">
				<Icon className="text-primary size-4" />
			</div>
			{type === "audio" ? (
				<AudioPlayer file={file} />
			) : (
				<p className="text-primary flex-1 self-center font-semibold wrap-anywhere">{file.name}</p>
			)}
			<button
				type="button"
				className="cursor-pointer rounded-full bg-black/30 p-2 transition-all duration-100 hover:bg-black/50"
				onClick={event => {
					event.stopPropagation()
					onChange(null)
				}}
			>
				<DeleteIcon className="text-danger size-4" />
			</button>
			<div className="bg-primary rounded-full p-1">
				<CheckmarkIcon className="size-2 text-white" />
			</div>
		</div>
	)
}

const AudioPlayer: FC<{ file: File }> = ({ file }) => {
	const fileUrl = useFileUrl(file)

	const extension = getExtensionByFileName(file.name)?.toUpperCase()

	return (
		<div className="flex-1 space-y-3 self-center">
			<p className="text-primary font-semibold wrap-anywhere">{extension ? `${extension} file` : "Audio file"}</p>
			<audio controls className="w-full max-w-md" src={fileUrl} />
		</div>
	)
}

export default FileChooser
